import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Grid, TextField, InputAdornment, Alert, Slider } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * Budgeting Style Options
 */
const BUDGETING_STYLES = {
  '50_30_20': {
    name: '50/30/20 Rule',
    description: '50% Needs, 30% Wants, 20% Savings',
    needs: 50,
    wants: 30,
    savings: 20
  },
  '60_20_20': {
    name: '60/20/20 Rule',
    description: '60% Needs, 20% Wants, 20% Savings',
    needs: 60,
    wants: 20,
    savings: 20
  },
  '70_20_10': {
    name: '70/20/10 Rule',
    description: '70% Needs, 20% Wants, 10% Savings',
    needs: 70,
    wants: 20,
    savings: 10
  },
  'zero_based': {
    name: 'Zero-Based Budgeting',
    description: 'Every dollar assigned, maximize savings',
    needs: 0,
    wants: 0,
    savings: 0 // Custom calculation
  },
  'envelope': {
    name: 'Envelope Method',
    description: 'Fixed categories with strict limits',
    needs: 0,
    wants: 0,
    savings: 0 // Custom calculation
  },
  'custom': {
    name: 'Custom',
    description: 'Set your own percentages',
    needs: 0,
    wants: 0,
    savings: 0
  }
};

/**
 * Budgeting Style Selector Component
 * 🎯 Helps users select and configure budgeting styles
 */
const BudgetingStyleSelector = ({
  selectedStyle = '50_30_20',
  monthlyIncome = 0, // Net income (used for calculations)
  grossMonthlyIncome = 0, // Gross income (for display)
  monthlyExpenses = 0,
  monthlySavings = 0,
  onStyleChange,
  onExpensesChange,
  onSavingsChange,
  customPercentages = { needs: 50, wants: 30, savings: 20 },
  onCustomPercentagesChange
}) => {
  const style = BUDGETING_STYLES[selectedStyle] || BUDGETING_STYLES['50_30_20'];
  
  // Calculate recommended values based on style
  const calculateRecommended = () => {
    if (!monthlyIncome || monthlyIncome <= 0) {
      return { expenses: 0, savings: 0 };
    }

    if (selectedStyle === 'zero_based' || selectedStyle === 'envelope') {
      // For zero-based and envelope, recommend 20% savings
      return {
        expenses: monthlyIncome * 0.8,
        savings: monthlyIncome * 0.2
      };
    }

    if (selectedStyle === 'custom') {
      const needsAmount = monthlyIncome * (customPercentages.needs / 100);
      const wantsAmount = monthlyIncome * (customPercentages.wants / 100);
      return {
        expenses: needsAmount + wantsAmount,
        savings: monthlyIncome * (customPercentages.savings / 100)
      };
    }

    // Standard percentage-based rules
    const needsAmount = monthlyIncome * (style.needs / 100);
    const wantsAmount = monthlyIncome * (style.wants / 100);
    return {
      expenses: needsAmount + wantsAmount,
      savings: monthlyIncome * (style.savings / 100)
    };
  };

  const recommended = calculateRecommended();
  const isInRed = monthlyExpenses > monthlyIncome;
  const savingsRate = monthlyIncome > 0 ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100 : 0;
  const isUnsustainable = savingsRate < 0 || monthlyExpenses > monthlyIncome * 0.95;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Budgeting Style & Spending
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a budgeting style to automatically adjust your spending and savings recommendations
      </Typography>

      {/* Budgeting Style Selector */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Budgeting Style</InputLabel>
        <Select
          value={selectedStyle}
          label="Budgeting Style"
          onChange={(e) => onStyleChange(e.target.value)}
        >
          {Object.entries(BUDGETING_STYLES).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              <Box>
                <Typography variant="body1">{value.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {value.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Custom Percentages (only for custom style) */}
      {selectedStyle === 'custom' && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Custom Percentages
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Needs %"
                type="number"
                value={customPercentages.needs}
                onChange={(e) => onCustomPercentagesChange({
                  ...customPercentages,
                  needs: parseFloat(e.target.value) || 0
                })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                size="small"
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Wants %"
                type="number"
                value={customPercentages.wants}
                onChange={(e) => onCustomPercentagesChange({
                  ...customPercentages,
                  wants: parseFloat(e.target.value) || 0
                })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                size="small"
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Savings %"
                type="number"
                value={customPercentages.savings}
                onChange={(e) => onCustomPercentagesChange({
                  ...customPercentages,
                  savings: parseFloat(e.target.value) || 0
                })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                size="small"
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Total: {(customPercentages.needs + customPercentages.wants + customPercentages.savings).toFixed(0)}%
          </Typography>
        </Box>
      )}

      {/* Monthly Income Display */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Monthly Income
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Gross (Annual / 12)
            </Typography>
            <Typography variant="h6" color="text.secondary">
              ${(grossMonthlyIncome || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Net (Used for Calculations)
            </Typography>
            <Typography variant="h6" color="primary.main">
              ${(monthlyIncome || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Monthly Expenses Slider */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Monthly Expenses
          </Typography>
          <Typography 
            variant="subtitle2" 
            color={isInRed ? 'error.main' : 'text.primary'}
            sx={{ fontWeight: isInRed ? 600 : 400 }}
          >
            ${monthlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {isInRed && ' ⚠️'}
          </Typography>
        </Box>
        <Slider
          value={typeof monthlyExpenses === 'number' ? monthlyExpenses : 0}
          onChange={(e, newValue) => {
            e.preventDefault();
            onExpensesChange(newValue);
          }}
          onChangeCommitted={(e, newValue) => {
            onExpensesChange(newValue);
          }}
          min={0}
          max={Math.max(monthlyIncome * 1.2, monthlyExpenses * 1.5, 50000)} // Allow up to 20% over income to show red zone
          step={50}
          marks={[
            { value: 0, label: '$0' },
            { value: monthlyIncome, label: `Income: $${monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` },
            { value: Math.max(monthlyIncome * 1.2, monthlyExpenses * 1.5, 50000), label: `$${Math.max(monthlyIncome * 1.2, monthlyExpenses * 1.5, 50000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }
          ]}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: isInRed ? 'error.main' : 'primary.main'
            },
            '& .MuiSlider-track': {
              backgroundColor: isInRed ? 'error.main' : 'primary.main'
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Recommended: ${recommended.expenses.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Typography>
          <Typography 
            variant="caption" 
            color={isInRed ? 'error.main' : 'text.secondary'}
            sx={{ fontWeight: isInRed ? 600 : 400 }}
          >
            {isInRed ? 'Over Budget!' : `${((monthlyExpenses / monthlyIncome) * 100).toFixed(0)}% of income`}
          </Typography>
        </Box>
      </Box>

      {/* Monthly Savings Slider */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Monthly Savings
          </Typography>
          <Typography 
            variant="subtitle2" 
            color={isUnsustainable ? 'error.main' : 'success.main'}
            sx={{ fontWeight: 600 }}
          >
            ${monthlySavings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: isUnsustainable ? 'error.50' : 'success.50', borderRadius: 1, border: '1px solid', borderColor: isUnsustainable ? 'error.200' : 'success.200', mb: 1 }}>
          <Typography variant="body2" color={isUnsustainable ? 'error.main' : 'success.main'}>
            {isUnsustainable 
              ? `⚠️ Unsustainable: You're spending ${monthlyIncome > 0 ? ((monthlyExpenses / monthlyIncome) * 100).toFixed(0) : 0}% of income. Savings: $${(monthlyIncome - monthlyExpenses).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
              : `Recommended: $${recommended.savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            }
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Savings Rate: {savingsRate.toFixed(1)}% | Remaining: ${(monthlyIncome - monthlyExpenses).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Slider
          value={typeof monthlySavings === 'number' ? monthlySavings : 0}
          onChange={(e, newValue) => {
            e.preventDefault();
            onSavingsChange(newValue);
          }}
          onChangeCommitted={(e, newValue) => {
            onSavingsChange(newValue);
          }}
          min={0}
          max={Math.max(monthlyIncome, monthlySavings * 1.5, recommended.savings * 2, 10000)}
          step={50}
          marks={[
            { value: 0, label: '$0' },
            { value: recommended.savings > 0 ? recommended.savings : monthlyIncome * 0.2, label: `Recommended: $${(recommended.savings > 0 ? recommended.savings : monthlyIncome * 0.2).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` },
            { value: Math.max(monthlyIncome, monthlySavings * 1.5, recommended.savings * 2, 10000), label: `$${Math.max(monthlyIncome, monthlySavings * 1.5, recommended.savings * 2, 10000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }
          ]}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: isUnsustainable ? 'error.main' : 'success.main'
            },
            '& .MuiSlider-track': {
              backgroundColor: isUnsustainable ? 'error.main' : 'success.main'
            }
          }}
        />
      </Box>

      {/* Red Zone Alert */}
      {isInRed && (
        <Alert severity="error" icon={<InfoIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            You're spending more than you earn!
          </Typography>
          <Typography variant="caption">
            Monthly expenses (${monthlyExpenses.toLocaleString()}) exceed monthly income (${monthlyIncome.toLocaleString()}). 
            This is unsustainable. Consider reducing expenses or increasing income.
          </Typography>
        </Alert>
      )}

      {/* Unsustainable Warning */}
      {isUnsustainable && !isInRed && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Your savings rate is very low ({savingsRate.toFixed(1)}%). Consider reducing expenses to increase savings.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default BudgetingStyleSelector;

